import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import {
  DISMISS_LABEL, GRANT_READ_ACCESS_LABEL, SETUP_CHOICES, SWITCH_UNKNOWN_NOTE, STRIP_SIGN_IN_APP_LABEL, canChangePhoneSource, consentOutcome, isAppIdFieldError, isConsentUrl,
  isEntraSource, isMethodIdFieldError, isTenantId,
  keyExpiryText, lastMoveNote, moveView, normaliseTenantId, panelPick, PHONE_SOURCE_LABELS, phoneSourceEntraNote, phoneSourceMoveAction, phoneSourceMoveEnd,
  portalMoveLine, readAccessSettled, readPendingSource, reloadAfterError, signInAppStrip, signInChangeShown, signInMoveEnd, signInPanelOpen, signInRefusal, statusView, writePendingSource,
  type LastMove,
} from "../lib/eam";
import EamSteps from "../components/EamSteps";
import EamUsersCard from "../components/EamUsersCard";
import PhoneSourceOptions from "../components/PhoneSourceOptions";
import SetupChoiceOptions from "../components/SetupChoiceOptions";
import SignInAppPanel from "../components/SignInAppPanel";
import type { EamEntraSource, EamPhoneSource, EamReadAccess, EamSignInApp, EamUser, EamView } from "../types/api";

const POLL_MS = 10_000;

// Reading window.localStorage can itself throw (blocked site data).
function pendingStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const PRIMARY_BUTTON =
  "inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
const CARD = "bg-white rounded-xl shadow-card border border-gray-100 p-6";

function Spinner({ small = false }: { small?: boolean }) {
  return (
    <div
      className={`inline-block animate-spin rounded-full border-accent border-t-transparent ${small ? "h-5 w-5 border-2" : "h-8 w-8 border-4"}`}
    />
  );
}

export default function EntraMfaPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { search } = useLocation();
  const [view, setView] = useState<EamView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [choiceId, setChoiceId] = useState<(typeof SETUP_CHOICES)[number]["id"]>("property");
  const [consent, setConsent] = useState(() => consentOutcome(search));
  const [changing, setChanging] = useState(false);
  const [newSource, setNewSource] = useState<EamPhoneSource>("property");
  // Null until the admin opens the panel; `target` is the target it opens on. It also shows during a move.
  const [signInPanel, setSignInPanel] = useState<{ target: EamSignInApp | null } | null>(null);
  const [lastMove, setLastMove] = useState<LastMove | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [users, setUsers] = useState<EamUser[] | null>(null);
  // Null while the check runs; a failed call reads as "not known".
  const [readAccess, setReadAccess] = useState<EamReadAccess | null>(null);
  const [afterConsent] = useState(() => consent.kind === "returned");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A running sign-in move closes the phone-source panel, so it does not come back with an old choice.
  const applyView = useCallback((next: EamView) => {
    setView(next);
    if (next.realm && moveView(next.realm)) {
      setChanging(false);
      writePendingSource(pendingStorage(), null);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      applyView(await api.getEam());
      setError(null);
      setErrorStatus(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load Microsoft Entra MFA.");
      setErrorStatus(e instanceof ApiError ? e.status : null);
    } finally {
      setLoading(false);
    }
  }, [applyView]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional fetch-on-auth-ready, like the API Keys page
      void load();
    }
  }, [isAuthenticated, authLoading, load]);

  const deploying = view?.status === "activating";
  useEffect(() => {
    if (!deploying) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [deploying, load]);

  // The key task writes the expiry a little after the realm is ready: look again a few times
  // (not forever) so the summary strip fills in without a manual reload.
  const missingExpiry = view?.status !== "activating" && !!view?.realm && !view.realm.key_expires_at;
  useEffect(() => {
    if (!missingExpiry) return;
    let left = 6;
    const timer = setInterval(() => {
      void load();
      if (--left === 0) clearInterval(timer);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [missingExpiry, load]);

  // Calls overlap (load, visibility, poll): only the latest call's answer is applied.
  const readAccessCall = useRef(0);
  const loadReadAccess = useCallback(async (): Promise<EamReadAccess> => {
    const call = ++readAccessCall.current;
    let next: EamReadAccess;
    try {
      next = await api.getEamReadAccess();
    } catch {
      next = { status: "unknown", roles: [] };
    }
    if (mounted.current && call === readAccessCall.current) setReadAccess(next);
    return next;
  }, []);

  const hasRealm = !!view?.realm;
  const phoneSource = view?.realm?.phone_source ?? null;
  // The panel's target when it is a move to an Entra source.
  const pendingSource: EamEntraSource | null =
    changing && isEntraSource(newSource) && newSource !== phoneSource ? newSource : null;
  const signInKey = `${view?.realm?.sign_in_app?.app}|${view?.realm?.sign_in_app?.app_id}|${view?.realm?.sign_in_app?.moving_to?.app}`;
  useEffect(() => {
    if (!hasRealm) return;
    void loadReadAccess();
  }, [hasRealm, phoneSource, signInKey, loadReadAccess]);

  // The admin may come back from the Grant link in another tab: reopen the panel at the target.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !view?.realm) return;
    restored.current = true;
    const stored = readPendingSource(pendingStorage());
    if (!stored) return;
    if (stored !== view.realm.phone_source && canChangePhoneSource(view.realm)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time restore once the view is there
      setNewSource(stored);
      setChanging(true);
    } else {
      writePendingSource(pendingStorage(), null);
    }
  }, [view]);

  // The consent link opens in a new tab: look again when this one is shown.
  useEffect(() => {
    if (!hasRealm) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void loadReadAccess();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [hasRealm, loadReadAccess]);

  // The poll reads the target from a ref: a radio change must not reset its six ticks.
  const pendingRef = useRef<EamEntraSource | null>(null);
  useEffect(() => {
    pendingRef.current = pendingSource;
  }, [pendingSource]);

  // Microsoft grants the roles a moment after the redirect: look again a few times, until the roles
  // of the source (or the one being moved to) are all there.
  useEffect(() => {
    if (!afterConsent || !hasRealm) return;
    let left = 6;
    let stopped = false;
    const timer = setInterval(() => {
      void loadReadAccess().then((access) => {
        if (stopped) return;
        if (readAccessSettled(access, phoneSource, pendingRef.current)) clearInterval(timer);
      });
      if (--left === 0) clearInterval(timer);
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [afterConsent, hasRealm, phoneSource, loadReadAccess]);

  // A 409 means someone else got there first: just show what exists now (not for no_company_app).
  // An error that `isFieldError` accepts goes to the caller, to show under its field.
  const run = async (
    action: () => Promise<EamView>,
    isFieldError?: (code: string | null) => boolean,
    signInApp = false,
  ): Promise<{ ok: boolean; fieldError: string | null }> => {
    setBusy(true);
    setError(null);
    setErrorStatus(null);
    setRefusal(null);
    setLastMove(null);
    try {
      applyView(await action());
      return { ok: true, fieldError: null };
    } catch (e) {
      if (e instanceof ApiError && isFieldError?.(e.error)) return { ok: false, fieldError: e.message };
      if (e instanceof ApiError && e.status === 409 && e.error === "read_access_missing") void loadReadAccess();
      if (e instanceof ApiError && reloadAfterError(e.status, e.error)) {
        await load();
        if (signInApp) setRefusal(signInRefusal(e.status, e.error));
      } else {
        setError(e instanceof Error ? e.message : "Something went wrong.");
        setErrorStatus(e instanceof ApiError ? e.status : null);
      }
      return { ok: false, fieldError: null };
    } finally {
      setBusy(false);
    }
  };

  const saveOwnApp = async (appId: string): Promise<string | null> => {
    const result = await run(() => api.saveEamSignInApp("customer", appId), isAppIdFieldError, true);
    if (result.ok) {
      setSignInPanel(null);
    }
    return result.fieldError;
  };

  const saveMethodId = (methodId: string) => run(() => api.saveEamMethodId(methodId), isMethodIdFieldError);

  const endMove = async (action: () => Promise<EamView>, outcome: "finished" | "cancelled") => {
    const ended = view?.realm ? signInMoveEnd(view.realm, outcome) : null;
    if ((await run(action, undefined, true)).ok) {
      setLastMove(ended);
      setSignInPanel(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="text-center py-12">
        <Spinner />
        <p className="mt-2 text-gray-500">Loading...</p>
      </div>
    );
  }

  const tenant = normaliseTenantId(tenantId);
  const show = view ? statusView(view.status) : null;
  const choice = SETUP_CHOICES.find((c) => c.id === choiceId) ?? SETUP_CHOICES[0];
  const realm = view?.realm ?? null;
  const strip = realm ? signInAppStrip(realm) : null;
  const readConsentUrl = isConsentUrl(view?.read_consent_url ?? null) ? view?.read_consent_url : null;
  const moveAction = pendingSource ? phoneSourceMoveAction(readAccess, pendingSource, readConsentUrl) : null;
  // The source whose numbers the Users card compares with: the one being moved to once its roles are all present.
  const compareSource: EamEntraSource | null =
    moveAction === "switch" ? pendingSource : isEntraSource(phoneSource) ? phoneSource : null;
  const comparisonKey = `${readAccess?.status ?? ""}|${readAccess?.roles.join(",") ?? ""}`;
  const closePhoneSource = () => {
    setChanging(false);
    writePendingSource(pendingStorage(), null);
  };
  const savePhoneSource = () => {
    const ended = phoneSourceMoveEnd(realm?.phone_source ?? null, newSource);
    void run(() => api.setEamPhoneSource(newSource)).then((r) => {
      if (!r.ok) return;
      closePhoneSource();
      setLastMove(ended);
      void loadReadAccess();
    });
  };
  const panelOpen = signInPanelOpen(realm, deploying, signInPanel !== null);
  const panelShown = !!(view?.realm && view.entra && show?.summary && panelOpen);
  const pickedSignInApp = panelShown && realm ? panelPick(realm, signInPanel?.target ?? null) : null;

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Microsoft Entra MFA</h2>
          <p className="mt-1 text-sm text-gray-500">
            {show?.form || !view
              ? "Use Mideye (Touch, SMS, hardware token) as an external MFA method in your Microsoft Entra tenant. Mideye hosts it; you only add it in Entra."
              : "Do these steps in your Microsoft Entra admin center."}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
          <h3 className="text-sm font-medium text-red-800">Error</h3>
          <p className="mt-1 text-sm text-red-700">{error}</p>
          {!view && errorStatus !== 403 && errorStatus !== 503 && (
            <button
              onClick={() => void load()}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
            >
              Try again
            </button>
          )}
        </div>
      )}

      {consent.kind === "returned" && (
        <div className="mb-6 flex items-start justify-between gap-3 rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">Consent returned from Microsoft</p>
          <button
            onClick={() => setConsent({ kind: "none" })}
            className="text-sm font-medium text-green-700 hover:text-green-600"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {consent.kind === "error" && (
        <div className="mb-6 flex items-start justify-between gap-3 rounded-md bg-amber-50 p-4" role="alert">
          <p className="text-sm text-amber-800">
            Consent did not complete ({consent.aadsts ? `${consent.code}, ${consent.aadsts}` : consent.code}).
            Try again from a normal browser window, or grant consent in Entra: App registrations → Mideye
            EAM → API permissions → Grant admin consent.
          </p>
          <button
            onClick={() => setConsent({ kind: "none" })}
            className="text-sm font-medium text-amber-700 hover:text-amber-600"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {refusal && (
        <div className="mb-6 flex items-start justify-between gap-3 rounded-md bg-amber-50 p-4" role="alert">
          <p className="text-sm text-amber-800">{refusal}</p>
          <button onClick={() => setRefusal(null)} className="text-sm font-medium text-amber-700 hover:text-amber-600">
            {DISMISS_LABEL}
          </button>
        </div>
      )}

      {view && show?.form && (
        <form
          className={`${CARD} max-w-2xl`}
          onSubmit={(e) => {
            e.preventDefault();
            if (isTenantId(tenant)) void run(() => api.createEamRealm(tenant, choice.phoneSource, choice.signInApp));
          }}
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">Set up Mideye for your tenant</h3>
          <div className="space-y-5">
            <div>
              <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700">
                Microsoft Entra tenant ID
              </label>
              <input
                id="tenantId"
                className="input mt-1 font-mono"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-gray-500">Entra admin center → Overview → Tenant ID</p>
            </div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700">
                Where are your users' mobile numbers?
              </legend>
              <SetupChoiceOptions name="setupChoice" value={choiceId} onChange={setChoiceId} />
            </fieldset>
            <div className="flex justify-end pt-2">
              <button type="submit" className={PRIMARY_BUTTON} disabled={busy || !isTenantId(tenant)}>
                {busy ? "Creating..." : "Create realm"}
              </button>
            </div>
          </div>
        </form>
      )}

      {view?.realm && show?.summary && (
        <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-card text-sm">
          <span className="inline-flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-400">Realm</span>
            <span className="font-mono text-gray-700">{view.realm.id}</span>
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-400">Tenant</span>
            <span className="font-mono text-gray-700 break-all">{view.realm.tenant_id}</span>
          </span>
          {view.realm.phone_source && (
            <span className="inline-flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-400">Mobile numbers</span>
              <span className="text-gray-700">{PHONE_SOURCE_LABELS[view.realm.phone_source]}</span>
              {!deploying && !changing && canChangePhoneSource(view.realm) && (
                <button
                  className="text-sm font-medium text-accent hover:text-accent-dark"
                  onClick={() => {
                    setNewSource(view.realm?.phone_source ?? "property");
                    setChanging(true);
                  }}
                >
                  Change
                </button>
              )}
            </span>
          )}
          {strip && (
            <span className="inline-flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-400">{STRIP_SIGN_IN_APP_LABEL}</span>
              <span className="text-gray-700">{strip.text}</span>
              {signInChangeShown(view.realm, view.status) && !signInPanel && (
                <button
                  className="text-sm font-medium text-accent hover:text-accent-dark"
                  onClick={() => setSignInPanel({ target: null })}
                >
                  Change
                </button>
              )}
            </span>
          )}
          {keyExpiryText(view.realm) && <span className="text-gray-700">{keyExpiryText(view.realm)}</span>}
          <span className="ml-auto text-xs text-gray-400">
            Key "{view.realm.key_label}" is on your API Keys page
          </span>
        </div>
      )}

      {panelShown && view?.realm && view.entra && (
        <SignInAppPanel
          realm={view.realm}
          redirectUri={view.entra.redirect_uri}
          onReload={() => void load()}
          takeFocus={signInPanel !== null}
          mideyeAppConsentUrl={view.mideye_app_consent_url}
          pick={signInPanel?.target ?? null}
          onPick={(target) => setSignInPanel({ target })}
          busy={busy}
          onSaveOwnApp={saveOwnApp}
          onSaveMideye={() =>
            void run(() => api.saveEamSignInApp("mideye"), undefined, true).then((r) => {
              if (r.ok) setSignInPanel(null);
            })
          }
          onFinish={(appId) => void endMove(() => api.finishEamSignInMove(appId), "finished")}
          onCancelMove={(appId) => void endMove(() => api.cancelEamSignInMove(appId), "cancelled")}
          onClose={() => setSignInPanel(null)}
        />
      )}

      {view?.realm && show?.summary && changing && !deploying && canChangePhoneSource(view.realm) && (
        <fieldset className={`${CARD} mb-6`}>
          <legend className="sr-only">Where are your users' mobile numbers?</legend>
          <h3 className="text-sm font-medium text-gray-700">Where are your users' mobile numbers?</h3>
          <PhoneSourceOptions name="changePhoneSource" value={newSource} onChange={setNewSource} entraNote={phoneSourceEntraNote(view.realm) ?? undefined} signInApp={view.realm.sign_in_app?.app ?? null} />
          {newSource === "portal" && isEntraSource(view.realm.phone_source) && (
            <p className="mt-3 text-sm text-gray-700">{portalMoveLine(users ? users.length : null)}</p>
          )}
          {moveAction === "switch-unknown" && <p className="mt-3 text-sm text-gray-700">{SWITCH_UNKNOWN_NOTE}</p>}
          <div className="mt-4 flex justify-end gap-3">
            <button className="text-sm font-medium text-gray-600 hover:text-gray-800" onClick={closePhoneSource}>
              Cancel
            </button>
            {moveAction === null && (
              <button className={PRIMARY_BUTTON} disabled={busy} onClick={savePhoneSource}>
                {busy ? "Saving..." : "Save"}
              </button>
            )}
            {moveAction === "grant" && pendingSource && (
              <a
                href={readConsentUrl ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={PRIMARY_BUTTON}
                onClick={() => writePendingSource(pendingStorage(), pendingSource)}
                onAuxClick={() => writePendingSource(pendingStorage(), pendingSource)}
              >
                {GRANT_READ_ACCESS_LABEL}
              </a>
            )}
            {(moveAction === "switch" || moveAction === "switch-unknown") && (
              <button className={PRIMARY_BUTTON} disabled={busy} onClick={savePhoneSource}>
                {busy ? "Saving..." : `Switch to ${PHONE_SOURCE_LABELS[newSource]}`}
              </button>
            )}
          </div>
        </fieldset>
      )}

      {show?.deploying && (
        <div className={`${CARD} flex items-center gap-4 max-w-2xl`} role="status">
          <Spinner />
          <p className="text-sm text-gray-700">
            Your realm is being deployed. This takes up to 2 minutes.
          </p>
        </div>
      )}

      {show?.waiting && (
        <div className={`${CARD} max-w-2xl`} role="status">
          <p className="text-sm text-gray-700">
            Your realm is ready, but registering it with Microsoft is taking longer than expected.
            Mideye has been notified; you can come back later.
          </p>
        </div>
      )}

      {show?.unfinished && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 max-w-2xl">
          <h3 className="text-base font-semibold text-amber-900">Setup did not finish</h3>
          <p className="mt-2 text-sm text-amber-800">
            Your realm was created but is not ready yet. Finish the setup to try again.
          </p>
          <button
            className={`${PRIMARY_BUTTON} mt-4`}
            disabled={busy}
            onClick={() => void run(() => api.finishEamRealm())}
          >
            {busy ? "Finishing..." : "Finish setup"}
          </button>
        </div>
      )}

      {show?.steps && view?.entra && view.realm && (
        <EamSteps
          realm={view.realm}
          entra={view.entra}
          consentUrl={view.consent_url}
          note={lastMoveNote(lastMove, view.realm)}
          onDismissNote={() => setLastMove(null)}
          busy={busy}
          onSaveOwnApp={saveOwnApp}
          onChangeId={() => setSignInPanel({ target: "customer" })}
          users={users}
          onSaveMethodId={saveMethodId}
          readAccess={readAccess}
          pendingSource={pendingSource}
          pickedSignInApp={pickedSignInApp}
        />
      )}

      {view?.realm && (
        <EamUsersCard
          onUsers={setUsers}
          phoneSource={view.realm.phone_source}
          compareSource={compareSource}
          comparisonKey={comparisonKey}
          onReadAccessStale={() => void loadReadAccess()}
        />
      )}
    </div>
  );
}
